import { useMemo } from "react";
import { useLingui } from "@lingui/react";
import { BaseEditableTable } from "@/components/table/BaseEditableTable";
import type { EditableCellConfig } from "@/components/table/types";
import { formatMonthYear } from "@/lib/format";
import { formatMetricCsvValue } from "@/lib/metrics/csv";
import { formatMetricValue, metricStatDelta } from "@/lib/metrics/metricFormat";
import { metricDelta } from "@/lib/metrics/period";
import type { MetricDefinition, MetricPoint } from "@/lib/metrics/types";
import type { ExportColumn } from "@/utils/cockpit-export";
import { chartParts } from "./MetricChart";

const noop = async () => {};

export interface MonthRow {
  id: string;
  period: string;
  value: number | null;
  previous: number | null;
  parts: Record<string, number>;
}

export function monthRows(points: readonly MetricPoint[]): MonthRow[] {
  return points.map((p) => ({
    id: p.period,
    period: p.period,
    value: p.value,
    previous: p.previous,
    parts: p.breakdown ?? {},
  }));
}

/** The table columns and the export columns of the month view, from one definition. */
export function useMonthColumns(def: MetricDefinition, points: readonly MetricPoint[], currency: string) {
  const { i18n } = useLingui();
  const parts = useMemo(
    () => (def.breakdownLabels ? chartParts({ ...def, stacked: true }, points) : []),
    [def, points],
  );
  const partName = (k: string) => (def.breakdownLabels?.[k] ? i18n._(def.breakdownLabels[k]) : k);
  const headers = {
    month: i18n._({ id: "analytics.metrics.col.month", message: "Month" }),
    value: i18n._({ id: "analytics.metrics.col.value", message: "Value" }),
    previous: i18n._({ id: "analytics.metrics.previousYear", message: "Previous year" }),
    change: i18n._({ id: "analytics.metrics.col.change", message: "Change" }),
  };
  const columns = useMemo<EditableCellConfig<MonthRow>[]>(
    () => [
      {
        accessorKey: "period",
        header: headers.month,
        editable: false,
        sortable: true,
        cell: ({ row }) => formatMonthYear(row.original.period, i18n.locale, "long"),
      },
      {
        accessorKey: "value",
        header: headers.value,
        editable: false,
        cell: ({ row }) => formatMetricValue(def.unit, row.original.value, i18n, currency) || "–",
      },
      ...(def.forward
        ? []
        : ([
            {
              accessorKey: "previous",
              header: headers.previous,
              editable: false,
              cell: ({ row }: { row: { original: MonthRow } }) =>
                formatMetricValue(def.unit, row.original.previous, i18n, currency) || "–",
            },
            {
              accessorKey: "id",
              header: headers.change,
              editable: false,
              cell: ({ row }: { row: { original: MonthRow } }) =>
                metricStatDelta(def, metricDelta(def.unit, row.original.value, row.original.previous), i18n)?.value ??
                "–",
            },
          ] as EditableCellConfig<MonthRow>[])),
      ...parts.map(
        (k): EditableCellConfig<MonthRow> => ({
          accessorKey: "parts",
          id: `part_${k}`,
          header: partName(k),
          editable: false,
          cell: ({ row }) => formatMetricValue(def.unit, row.original.parts[k] ?? 0, i18n, currency),
        }),
      ),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [def, parts, currency, i18n.locale],
  );
  const exportColumns = useMemo<ExportColumn<MonthRow>[]>(
    () => [
      { key: "month", header: headers.month, accessor: (r) => r.period },
      { key: "value", header: headers.value, accessor: (r) => formatMetricCsvValue(def.unit, r.value) },
      ...(def.forward
        ? []
        : [{ key: "previous", header: headers.previous, accessor: (r: MonthRow) => formatMetricCsvValue(def.unit, r.previous) }]),
      ...parts.map((k) => ({
        key: `part_${k}`,
        header: partName(k),
        accessor: (r: MonthRow) => formatMetricCsvValue(def.unit, r.parts[k] ?? 0),
      })),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [def, parts, i18n.locale],
  );
  return { columns, exportColumns };
}

interface MetricMonthTableProps {
  def: MetricDefinition;
  rows: MonthRow[];
  columns: EditableCellConfig<MonthRow>[];
  isLoading: boolean;
}

/** The months of the chart as a table (#2175 B3): the values the chart draws, oldest first as the chart reads. */
export function MetricMonthTable({ def, rows, columns, isLoading }: MetricMonthTableProps) {
  return (
    <BaseEditableTable<MonthRow>
      data={rows}
      columns={columns}
      onCellUpdate={noop}
      isLoading={isLoading}
      defaultSortField="period"
      defaultSortOrder="asc"
      defaultPageSize={25}
      hidePagination={rows.length <= 25}
      tableId={`analytics-metric-months-${def.id}`}
    />
  );
}
